import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, inject, Inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import {
    ApiRequestService,
    BreadcrumbComponent,
    CookieComponent,
    CookieDialogConfiguration,
    CookieInputs,
    CookieObject,
    CookieService,
    DialogService,
    FooterComponent,
    HeaderComponent,
    LoadingOverlayComponent,
    NavigationMenuItem,
    SessionserviceService,
    SiteconfigService,
    SlideshowComponent,
    SubscriptionComponent,
    ToastmessageComponent,
    TranslatableComponent,
    TranslationPipe,
    TranslationService,
    TranslationStore
} from '@prekog/common-ui';
import { Observable } from 'rxjs';
import { AppConfigService } from './core/config/app-config.service';
import { Category } from './shared/category.model';
import { GetCategoryResponse } from './shared/get-category.response';
import { GetSiteConfigurationResponse } from './shared/get-site-configuration.response';
import { Material } from './shared/material.model';
import { SiteConfiguration } from './shared/site-configuration.model';
import { GetTranslationRequest } from './use-cases/translation/read-translation-request';
import { GetTranslationUseCase } from './use-cases/translation/read-translation-usecase';

@Component({
    selector: 'pf-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        HeaderComponent,
        LoadingOverlayComponent,
        SlideshowComponent,
        ToastmessageComponent,
        BreadcrumbComponent,
        RouterOutlet,
        FooterComponent,
        TranslationPipe,
        RouterLink,
        MatIconModule,
        MatTooltipModule
    ]
})

//TODO settingUpSubMenu() inside getSiteConfigurations has to be set back
export class AppComponent extends TranslatableComponent implements AfterViewInit {
    private _appConfigService = inject(AppConfigService);
    address: string | undefined;
    aszfUrl = '';
    canonicalUrl = window.location.origin + '/book';
    categoryList: Category[] = [];
    colorOfMobileSidenav = 'lightgreen';
    columnNumberOfSubmenu = 8;
    configuratorUrl = '';
    private readonly COOKIE_CONSENT_KEY = 'cookie_consent';
    cookieValue = '';
    dataManagamentUrl = '';
    descriptionOfTheSite = '';
    downloadFileUrl = '';
    email: string | undefined;
    facebookImgUrl = '';
    facebookUrl = 'https://www.facebook.com/profile.php?id=100089168181347';
    getCategoriesHavingProductsUrl = '/store/getCategoriesHavingProducts';
    getSiteconfigurationUrl = '/websiteconfigurator/get-site-configuration';
    logoUrl = '';
    nameOfTheSite = '';
    navigationMenu: NavigationMenuItem[] = [];
    openingHours: string[] | undefined;
    phoneNumber: string | undefined;
    production = false;
    products?: Material[];
    simplePayLogo = '';
    storeBaseUrl = '';
    private _dialogService = inject(DialogService);

    constructor(
        private _translateService: TranslationService,
        private _http: HttpClient,
        private _readTranslationfileUseCase: GetTranslationUseCase,
        public router: Router,
        private _apiRequestService: ApiRequestService,
        @Inject('PRODUCTION') private _production: boolean,
        @Inject('DOWNLOAD_FILE_URL') private _downloadFileUrl: string,
        @Inject('CONFIGURATOR_BASE_URL') private _configuratorUrl: string,
        @Inject('STORE_BACKEND_URL') private _storeBaseUrl: string,
        private cookieService: CookieService,
        private _siteConfigService: SiteconfigService,
        private _sessionService: SessionserviceService
    ) {
        super(_translateService, _http);
        this.production = _production;
        this.downloadFileUrl = _downloadFileUrl;
        this.simplePayLogo = this.downloadFileUrl + '/simplepaylogo';
        this.facebookImgUrl = this.downloadFileUrl + '/facebooklog';
        this.logoUrl = this.downloadFileUrl + '/multiverzumlogo';
        this.configuratorUrl = _configuratorUrl;
        this.storeBaseUrl = _storeBaseUrl;
    }

    ngAfterViewInit(): void { }

    override readTranslations(http: HttpClient, language: string): Observable<TranslationStore> {
        const request: GetTranslationRequest = { url: './assets/i18n/' + language + '.json' };
        this._readTranslationfileUseCase.execute(request) as unknown as Observable<TranslationStore>;
        return this._http.get<TranslationStore>('./assets/i18n/' + language + '.json');
    }

    override onInit(): void {
        this.getSiteConfiguration();
        this.settingUpNavigationMenu();
        this.cookieService.addDataLayer();
        this.cookieService.initGoogleConsent();
        const consent = JSON.parse(localStorage.getItem(this.COOKIE_CONSENT_KEY) as string) as CookieObject;
        if (consent && !this.isCookieExpired(consent.expiredTime)) {
            this.cookieService.acceptGoogleConsent(consent);
        } else {
            this.openCookie();
        }
        this.cookieService.loadGtagScripts(this._appConfigService.config.googleTagId);
    }

    isCookieExpired(expiredTime: Date | undefined): boolean {
        if (!expiredTime) {
            return true;
        }
        const expiredDate = new Date(expiredTime);
        const now = new Date();
        return expiredDate < now;
    }

    settingUpNavigationMenu() {
        this._http.get('./assets/configurations/navigation-menu.json').subscribe((response) => {
            this.navigationMenu = response as NavigationMenuItem[];
            this.settingUpSubmenu();
        });
    }

    settingUpSubmenu() {
        const submenuList: NavigationMenuItem[] = [];
        this._apiRequestService
            .sendRequest<{}, GetCategoryResponse>(this.storeBaseUrl, {}, true, this.getCategoriesHavingProductsUrl)
            .subscribe((response: GetCategoryResponse) => {
                this.categoryList = response.categoryList as Category[];
                const submenu: NavigationMenuItem = {
                    translationKey: this._translateService.translate('navigation.all'),
                    id: 'book',
                    isHidden: false,
                    isDefault: false,
                    hasDynamicSubMenu: false,
                    isStaticSubmenuVisible: false
                };
                submenuList.push(submenu);
                if (!this.categoryList || this.categoryList.length === 0) {
                    this.navigationMenu.forEach((navigationElement: NavigationMenuItem) => {
                        if (navigationElement.translationKey === this._translateService.translate('navigation.materials') && navigationElement.hasDynamicSubMenu) {
                            navigationElement.staticSubMenus = [...submenuList];
                            navigationElement.chunkedSubMenus = this.chunkArray(submenuList || [], this.columnNumberOfSubmenu);
                        }
                    });
                    return;
                }
                this.navigationMenu.forEach((navigationElement: NavigationMenuItem) => {
                    if (navigationElement.id === '') {
                        const index = this.navigationMenu.indexOf(navigationElement);
                        this.navigationMenu.splice(index, 1);
                    }
                });
                this.navigationMenu.forEach((navigationElement: NavigationMenuItem) => {
                    if (navigationElement.translationKey === this._translateService.translate('navigation.materials') && navigationElement.hasDynamicSubMenu) {
                        this.categoryList.forEach((category) => {
                            const categoryName = this.slugify(category.name!);
                            const submenu: NavigationMenuItem = {
                                translationKey: category.name,
                                id: 'category/' + categoryName + '/' + category.id,
                                isHidden: false,
                                isDefault: false,
                                hasDynamicSubMenu: false,
                                isStaticSubmenuVisible: false
                            };
                            submenuList.push(submenu);
                        });

                        navigationElement.staticSubMenus = [...submenuList];
                        navigationElement.chunkedSubMenus = this.chunkArray(submenuList || [], this.columnNumberOfSubmenu);
                    }
                });
            });
    }

    slugify(name: string): string {
        return name
            .normalize('NFD') // normalize accents
            .replace(/[\u0300-\u036f]/g, '') // remove accents
            .replace(/[^a-zA-Z0-9 ]/g, '') // remove non-alphanumerics
            .trim()
            .replace(/\s+/g, '-') // replace spaces with -
            .toLowerCase();
    }

    //it slices the mat-menu submenu into more columns with the given size number(this is the max number of navigationElements in a column)
    chunkArray<T>(arr: T[], size: number): T[][] {
        const result: T[][] = [];
        for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
        }
        return result;
    }

    getSiteConfiguration() {
        this._apiRequestService
            .sendRequest<{}, GetSiteConfigurationResponse>(this.configuratorUrl, {}, true, this.getSiteconfigurationUrl)
            .subscribe((response) => {
                const siteConfigurationList = response.siteConfigurationList as SiteConfiguration[];
                const siteConfigurationUrlList = response.siteConfigurationIsUrlList as SiteConfiguration[];

                let isSessionSaveForConfigurationEnded = false;

                let isSessionSaveForConfigurationUrlEnded = false;

                siteConfigurationList.forEach((siteconfig) => {
                    const value = siteconfig.configurationValue;
                    switch (siteconfig.configurationKey) {
                        case 'email':
                            this.email = value;
                            this._sessionService.set('email', value);
                            break;
                        case 'address':
                            this.address = value;
                            this._sessionService.set('address', value);
                            break;
                        case 'cellphone_number':
                            this.phoneNumber = value;
                            this._sessionService.set('cellphone_number', value);
                            break;
                        case 'opening_hours':
                            const formatted = this._siteConfigService.formatOpeningHours(value!);
                            this.openingHours = formatted;
                            this._sessionService.set('opening_hours', formatted);
                            break;
                        case 'introduction_text':
                            this._sessionService.set('introduction_text', value);
                            break;
                        case 'company_name':
                            this._sessionService.set('company_name', value);
                            break;
                        case 'company_registration_number':
                            this._sessionService.set('company_registration_number', value);
                            break;
                    }
                    isSessionSaveForConfigurationEnded = true;
                });

                siteConfigurationUrlList.forEach((siteconfig) => {
                    const value = siteconfig.configurationValue;
                    switch (siteconfig.configurationKey) {
                        case 'mobile_logo_url':
                            this._sessionService.set('mobile_logo_url', value);
                            break;
                        case 'logo_url':
                            this.logoUrl = value!;
                            this._sessionService.set('logo_url', value);
                            break;
                        case 'aszf_url':
                            this.aszfUrl = value!;
                            this._sessionService.set('aszf_url', value);
                            break;
                        case 'privacy_policy_url':
                            this.dataManagamentUrl = value!;
                            this._sessionService.set('privacy_policy_url', value);
                            break;
                    }
                    isSessionSaveForConfigurationUrlEnded = true;
                });
                if (isSessionSaveForConfigurationEnded && isSessionSaveForConfigurationUrlEnded) {
                    this._sessionService.setConfigLoaded();
                }
            });
    }

    openCookie() {
        const privacyPolicyUrl = JSON.parse(sessionStorage.getItem('privacy_policy_url') as string) as string;
        const cookiInputs: CookieInputs = {
            hasAnyOtherSettings: false,
            hasGoogleAds: true,
            hasGoogleAnalytics: true,
            backroundColor: '#e8fcfd',
            buttonColor: '#0E8388',
            buttonTextColor: 'white',
            privacyPolicyUrl: privacyPolicyUrl
        };
        const cookieDialogConfiguration: CookieDialogConfiguration = {
            cookieInputs: cookiInputs
        }
        this._dialogService.openDialog(CookieComponent, cookieDialogConfiguration);
    }

    openSubscriptionDialog(): void {
        this._dialogService.openDialog(SubscriptionComponent);
    }
}
